import type {
  Api,
  ClusterData,
  ClusterItem,
  ClusterSummary,
  IndustryIngest,
  IndustrySummary,
  ListReportsOptions,
  ReportPage,
  ResolvedCluster
} from "./api";
import {
  buildComparisonChart,
  chartsToCsv,
  columnValues,
  latestPercentile,
  parseScatterRows,
  readWorkbookTable,
  type CompanySeries,
  type WorkbookTable
} from "./chartParser";
import { buildIndustryCharts } from "./industryCharts";
import type { ChartConfig, PointMap } from "./industryParser";
import type { ChartSeries, ChartSpec, ReportDetail, ReportSummary, ScatterPoint } from "./types";

interface LocalIndustry {
  name: string;
  category?: string;
  charts: ChartConfig[];
  points: PointMap;
  reportId: string;
  updatedAt: string;
}

/**
 * In-memory, fully-offline implementation of the API used only in local mock
 * mode (VITE_LOCAL_MOCK=true). Tools (e.g. 시계열 종목비교) parse Excel in the
 * browser and create reports here, so the flow can be tested without any AWS
 * resources. Data lives only for the page session.
 */
export class LocalApiClient implements Api {
  private reports = new Map<string, ReportDetail>();
  private industries = new Map<string, LocalIndustry>();
  private clusters = new Map<string, ClusterData>();
  private categoryOrder: string[] = [];

  async listIndustries(): Promise<IndustrySummary[]> {
    return [...this.industries.entries()]
      .map(([industry_id, ind]) => ({
        industry_id,
        name: ind.name,
        category: ind.category,
        charts: ind.charts.length,
        weeks: countWeeks(ind.points),
        updated_at: ind.updatedAt,
        last_week: lastWeekOf(ind.points),
        report_id: ind.reportId
      }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }

  async ingestIndustry(input: IndustryIngest): Promise<IndustrySummary> {
    // Mirrors the server: an explicit industryId accumulates into that dataset;
    // otherwise a NEW industry gets an id that never collides with an existing
    // one (same name under a different category = its own dataset).
    let industryId = input.industryId?.trim() ?? "";
    if (!industryId) {
      const base =
        input.name.trim().toLowerCase().replace(/[^a-z0-9가-힣]+/gi, "-").replace(/^-+|-+$/g, "") ||
        `industry-${this.industries.size + 1}`;
      industryId = base;
      for (let i = 2; this.industries.has(industryId); i += 1) industryId = `${base}-${i}`;
    }
    const existing = this.industries.get(industryId);

    const points: PointMap = existing ? { ...existing.points } : {};
    for (const [chartId, byDate] of Object.entries(input.points)) {
      const merged = { ...(points[chartId] ?? {}) };
      for (const [date, values] of Object.entries(byDate)) merged[date] = { ...(merged[date] ?? {}), ...values };
      points[chartId] = merged;
    }

    const charts = buildIndustryCharts(input.charts, points);
    const now = new Date().toISOString();
    const reportId = existing?.reportId ?? `report_industry_${industryId}`;
    const category = input.category?.trim() || existing?.category;
    this.reports.set(reportId, {
      report_id: reportId,
      title: input.name,
      status: "published",
      created_at: now,
      published_at: now,
      source: "산업 분석",
      charts
    });
    this.industries.set(industryId, { name: input.name, category, charts: input.charts, points, reportId, updatedAt: now });

    return {
      industry_id: industryId,
      name: input.name,
      category,
      charts: input.charts.length,
      weeks: countWeeks(points),
      updated_at: now,
      last_week: lastWeekOf(points),
      report_id: reportId
    };
  }

  async updateIndustry(industryId: string, patch: { name?: string; category?: string }): Promise<IndustrySummary> {
    const ind = this.industries.get(industryId);
    if (!ind) throw new Error("Industry not found (local mock).");
    const name = patch.name?.trim();
    if (name) {
      ind.name = name;
      const report = this.reports.get(ind.reportId);
      if (report) this.reports.set(ind.reportId, { ...report, title: name });
    }
    if (patch.category !== undefined) ind.category = patch.category.trim() || undefined;
    ind.updatedAt = new Date().toISOString();
    this.industries.set(industryId, ind);
    return {
      industry_id: industryId,
      name: ind.name,
      category: ind.category,
      charts: ind.charts.length,
      weeks: countWeeks(ind.points),
      updated_at: ind.updatedAt,
      last_week: lastWeekOf(ind.points),
      report_id: ind.reportId
    };
  }

  async deleteIndustry(industryId: string): Promise<void> {
    const ind = this.industries.get(industryId);
    if (ind) this.reports.delete(ind.reportId);
    this.industries.delete(industryId);
  }

  async getCategoryOrder(): Promise<string[]> {
    return this.categoryOrder;
  }

  async setCategoryOrder(order: string[]): Promise<string[]> {
    this.categoryOrder = [...new Set(order.map((c) => c.trim()).filter(Boolean))];
    return this.categoryOrder;
  }

  async deleteCategory(name: string): Promise<number> {
    const cat = name.trim() || "미분류";
    let count = 0;
    for (const [id, ind] of [...this.industries.entries()]) {
      if ((ind.category?.trim() || "미분류") === cat) {
        this.reports.delete(ind.reportId);
        this.industries.delete(id);
        count += 1;
      }
    }
    this.categoryOrder = this.categoryOrder.filter((c) => c !== cat);
    return count;
  }

  async listClusters(): Promise<ClusterSummary[]> {
    // Preserve insertion/reorder order (no alpha sort), mirroring the server.
    return [...this.clusters.values()].map((c) => ({
      cluster_id: c.cluster_id,
      name: c.name,
      count: c.items.length,
      updated_at: c.updated_at
    }));
  }

  async getCluster(clusterId: string): Promise<ClusterData> {
    const c = this.clusters.get(clusterId);
    if (!c) throw new Error("Cluster not found.");
    return c;
  }

  async getClusterResolved(clusterId: string): Promise<ResolvedCluster> {
    const c = this.clusters.get(clusterId);
    if (!c) throw new Error("Cluster not found.");
    const charts: ResolvedCluster["charts"] = [];
    for (const item of c.items) {
      const ind = this.industries.get(item.industry_id);
      const report = ind ? this.reports.get(ind.reportId) : undefined;
      const chart = report?.charts.find((ch) => ch.id === item.chart_id);
      if (ind && chart) charts.push({ industry_id: item.industry_id, industry_name: ind.name, chart });
    }
    return { cluster_id: c.cluster_id, name: c.name, items: c.items, charts };
  }

  async createCluster(name: string): Promise<ClusterData> {
    const cleanName = name.trim() || "새 클러스터";
    const base = cleanName.toLowerCase().replace(/[^a-z0-9가-힣]+/gi, "-").replace(/^-+|-+$/g, "") || "cluster";
    let id = base;
    for (let i = 2; this.clusters.has(id); i += 1) id = `${base}-${i}`;
    const now = new Date().toISOString();
    const data: ClusterData = { cluster_id: id, name: cleanName, items: [], count: 0, updated_at: now };
    this.clusters.set(id, data);
    return data;
  }

  async updateCluster(clusterId: string, patch: { name?: string; items?: ClusterItem[] }): Promise<ClusterData> {
    const c = this.clusters.get(clusterId);
    if (!c) throw new Error("Cluster not found.");
    if (patch.name !== undefined && patch.name.trim()) c.name = patch.name.trim();
    if (patch.items !== undefined) c.items = dedupeItems(patch.items);
    c.count = c.items.length;
    c.updated_at = new Date().toISOString();
    this.clusters.set(clusterId, c);
    return c;
  }

  async addClusterItem(clusterId: string, item: ClusterItem): Promise<ClusterData> {
    const c = this.clusters.get(clusterId);
    if (!c) throw new Error("Cluster not found.");
    c.items = dedupeItems([...c.items, item]);
    c.count = c.items.length;
    c.updated_at = new Date().toISOString();
    this.clusters.set(clusterId, c);
    return c;
  }

  async deleteCluster(clusterId: string): Promise<void> {
    this.clusters.delete(clusterId);
  }

  async reorderClusters(orderedIds: string[]): Promise<ClusterSummary[]> {
    const entries = [...this.clusters.entries()];
    const byId = new Map(entries);
    const next = new Map<string, ClusterData>();
    for (const id of orderedIds) {
      const c = byId.get(id);
      if (c) {
        next.set(id, c);
        byId.delete(id);
      }
    }
    for (const [id, c] of byId) next.set(id, c);
    this.clusters = next;
    return [...this.clusters.values()].map((c) => ({
      cluster_id: c.cluster_id,
      name: c.name,
      count: c.items.length,
      updated_at: c.updated_at
    }));
  }

  async listReports(options: ListReportsOptions = {}): Promise<ReportPage> {
    // Insertion order of the Map is the display order (so drag-reorder persists).
    // The mock holds a single session's worth of reports, so it returns them all
    // in one page (no cursor).
    const items = [...this.reports.values()]
      .filter((report) => options.includeDrafts || report.status === "published")
      .map(toSummary);
    return { items, nextCursor: undefined };
  }

  async deleteReport(reportId: string): Promise<void> {
    this.reports.delete(reportId);
  }

  async updateReport(
    reportId: string,
    payload: { title: string; charts: ChartSpec[]; category?: string }
  ): Promise<void> {
    const existing = this.reports.get(reportId);
    if (!existing) throw new Error("Report not found (local mock).");
    const category = payload.category !== undefined ? payload.category.trim() || undefined : existing.category;
    this.reports.set(reportId, { ...existing, title: payload.title, charts: payload.charts, category });
  }

  async reorderReports(orderedIds: string[]): Promise<void> {
    const next = new Map<string, ReportDetail>();
    for (const id of orderedIds) {
      const report = this.reports.get(id);
      if (report) next.set(id, report);
    }
    // Keep any reports not included in the provided order (e.g. drafts) at the end.
    for (const [id, report] of this.reports) {
      if (!next.has(id)) next.set(id, report);
    }
    this.reports = next;
  }

  async getReport(reportId: string): Promise<ReportDetail> {
    const report = this.reports.get(reportId);
    if (!report) throw new Error("Report not found (local mock).");
    return report;
  }

  /**
   * Local-only: build the comparison charts (see buildComparisonCharts) and
   * publish them as a single report.
   */
  async createComparison(files: File[], options: ComparisonOptions = {}): Promise<void> {
    const { charts, title } = await buildComparisonCharts(files, options);
    const now = new Date().toISOString();
    const stamp = now.replace(/[-:TZ.]/g, "").slice(0, 14);
    const reportId = `report_compare_${stamp}`;
    this.reports.set(reportId, {
      report_id: reportId,
      title,
      status: "published",
      created_at: now,
      published_at: now,
      source: "시계열 종목비교",
      category: options.category?.trim() || undefined,
      charts
    });
  }

  /** Local-only: build a dual-axis chart and publish it as a report. */
  async createDualAxis(files: File[], options: DualAxisOptions = {}): Promise<void> {
    const { chart, title } = await buildDualAxisChart(files, options);
    const now = new Date().toISOString();
    const stamp = now.replace(/[-:TZ.]/g, "").slice(0, 14);
    const reportId = `report_dual_${stamp}`;
    this.reports.set(reportId, {
      report_id: reportId,
      title,
      status: "published",
      created_at: now,
      published_at: now,
      source: "이중축 시계열 비교",
      category: options.category?.trim() || undefined,
      charts: [chart]
    });
  }

  /** Local-only: build a quantile-vs-prediction scatter and publish it. */
  async createQuantileForecast(files: File[], options: QuantileForecastOptions = {}): Promise<void> {
    const { chart, title } = await buildQuantileForecastChart(files, options);
    const now = new Date().toISOString();
    const stamp = now.replace(/[-:TZ.]/g, "").slice(0, 14);
    const reportId = `report_quantile_${stamp}`;
    this.reports.set(reportId, {
      report_id: reportId,
      title,
      status: "published",
      created_at: now,
      published_at: now,
      source: "과거 분위 미래 예측",
      category: options.category?.trim() || undefined,
      charts: [chart]
    });
  }

  /** Local-only: industry scatter. Overwrites the same-titled chart (monthly). */
  async createScatter(files: File[], options: ScatterOptions = {}): Promise<void> {
    const { chart, title } = await buildScatterChart(files[0], options);
    for (const [rid, rep] of [...this.reports])
      if (rep.source === SCATTER_SOURCE && rep.title === title) this.reports.delete(rid);
    const now = new Date().toISOString();
    const reportId = `report_scatter_${now.replace(/[-:TZ.]/g, "").slice(0, 14)}`;
    this.reports.set(reportId, {
      report_id: reportId,
      title,
      status: "published",
      created_at: now,
      published_at: now,
      source: SCATTER_SOURCE,
      category: options.category?.trim() || undefined,
      charts: [chart]
    });
  }

  async getDownloadUrl(reportId: string): Promise<string> {
    const report = this.reports.get(reportId);
    if (!report) throw new Error("Report not found (local mock).");
    const csv = chartsToCsv(report.charts);
    return URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8" }));
  }
}

export interface ComparisonOptions {
  columns?: string[];
  columnTitles?: Record<string, string>;
  names?: string[];
  fromLabel?: string;
  toLabel?: string;
  yMin?: number;
  yMax?: number;
  title?: string;
  unit?: string;
  // Report list grouping label (publish-time).
  category?: string;
}

/**
 * Builds the comparison line charts WITHOUT publishing — one chart per selected
 * metric column, each overlaying all companies over the (sliced) time axis.
 * Shared by the live preview and createComparison.
 */
export async function buildComparisonCharts(
  files: File[],
  options: ComparisonOptions = {}
): Promise<{ charts: ChartSpec[]; title: string }> {
  const { columns, columnTitles, names, fromLabel, toLabel, yMin, yMax, title, unit } = options;

  const sources: Array<{ name: string; table: WorkbookTable }> = [];
  for (let i = 0; i < files.length; i++) {
    const file = files[i];
    const label = names?.[i]?.trim();
    const name = label || file.name.replace(/\.[^.]+$/, "");
    sources.push({ name, table: await readWorkbookTable(await file.arrayBuffer()) });
  }

  const firstCols = sources[0]?.table.columns ?? [];
  const picked = columns && columns.length ? columns : firstCols[0] ? [firstCols[0]] : [];
  if (!picked.length) {
    throw new Error("비교할 지표(열)를 찾지 못했습니다. 파일 형식을 확인하세요. (1열=기간, 그다음 열=지표)");
  }

  const unitValue = unit?.trim() || undefined;
  const charts: ChartSpec[] = [];
  for (const col of picked) {
    const colIndex = firstCols.indexOf(col);
    const companies: CompanySeries[] = [];
    for (const { name, table } of sources) {
      const values = columnValues(table, col, colIndex);
      if (table.labels.length && values && values.length) {
        companies.push({ name, labels: table.labels, values });
      }
    }
    if (!companies.length) continue;
    const chartTitle = columnTitles?.[col]?.trim() || col;
    const chart = sliceChartRange(buildComparisonChart(companies, chartTitle, unitValue), fromLabel, toLabel);
    if (Number.isFinite(yMin)) chart.yMin = yMin;
    if (Number.isFinite(yMax)) chart.yMax = yMax;
    charts.push(chart);
  }
  if (!charts.length) {
    throw new Error("선택한 지표/기간에서 데이터를 찾지 못했습니다.");
  }
  const finalTitle = title?.trim() || `${sources.map((s) => s.name).join(", ")} 비교`;
  return { charts, title: finalTitle };
}

export interface DualAxisOptions {
  names?: string[];
  leftColumn?: string;
  rightColumn?: string;
  leftTitle?: string;
  rightTitle?: string;
  leftUnit?: string;
  rightUnit?: string;
  leftMin?: number;
  leftMax?: number;
  rightMin?: number;
  rightMax?: number;
  fromLabel?: string;
  toLabel?: string;
  title?: string;
  // Report list grouping label (publish-time).
  category?: string;
}

const DUAL_AXIS_PALETTE = ["#1d4ed8", "#0f766e", "#b45309", "#7c3aed", "#be123c", "#0891b2"];

/**
 * Builds ONE dual y-axis chart (left + right): for each company a left-axis
 * series (solid) and a right-axis series (dashed), coloured per company. Used by
 * the dual-axis tool's preview and createDualAxis.
 */
export async function buildDualAxisChart(
  files: File[],
  options: DualAxisOptions = {}
): Promise<{ chart: ChartSpec; title: string }> {
  const { names, leftColumn, rightColumn, leftTitle, rightTitle, leftUnit, rightUnit } = options;
  const { leftMin, leftMax, rightMin, rightMax, fromLabel, toLabel, title } = options;

  if (!leftColumn || !rightColumn) {
    throw new Error("좌축·우축 지표를 모두 선택하세요.");
  }

  const sources: Array<{ name: string; table: WorkbookTable }> = [];
  for (let i = 0; i < files.length; i++) {
    const file = files[i];
    const label = names?.[i]?.trim();
    sources.push({
      name: label || file.name.replace(/\.[^.]+$/, ""),
      table: await readWorkbookTable(await file.arrayBuffer())
    });
  }

  const labels: string[] = [];
  for (const s of sources) {
    for (const l of s.table.labels) {
      if (!labels.includes(l)) labels.push(l);
    }
  }
  if (!labels.length) {
    throw new Error("시계열 데이터를 찾지 못했습니다.");
  }

  const leftName = leftTitle?.trim() || leftColumn;
  const rightName = rightTitle?.trim() || rightColumn;
  const firstCols = sources[0]?.table.columns ?? [];
  const leftIndex = firstCols.indexOf(leftColumn);
  const rightIndex = firstCols.indexOf(rightColumn);
  const series: ChartSeries[] = [];
  sources.forEach((s, i) => {
    const color = DUAL_AXIS_PALETTE[i % DUAL_AXIS_PALETTE.length];
    const leftVals = columnValues(s.table, leftColumn, leftIndex) ?? [];
    const rightVals = columnValues(s.table, rightColumn, rightIndex) ?? [];
    const leftByLabel = new Map(s.table.labels.map((l, idx) => [l, leftVals[idx] ?? 0]));
    const rightByLabel = new Map(s.table.labels.map((l, idx) => [l, rightVals[idx] ?? 0]));
    series.push({ name: `${s.name} · ${leftName}`, values: labels.map((l) => leftByLabel.get(l) ?? 0), axis: "left", color });
    series.push({ name: `${s.name} · ${rightName}`, values: labels.map((l) => rightByLabel.get(l) ?? 0), axis: "right", color, dashed: true });
  });

  const finalTitle = title?.trim() || `${sources.map((s) => s.name).join(", ")} · ${leftName} / ${rightName}`;
  let chart: ChartSpec = {
    id: `dual_${Date.now()}`,
    title: finalTitle,
    labels,
    series,
    kind: "line",
    dualAxis: true,
    unit: leftUnit?.trim() || undefined,
    rightUnit: rightUnit?.trim() || undefined,
    yMin: Number.isFinite(leftMin) ? leftMin : undefined,
    yMax: Number.isFinite(leftMax) ? leftMax : undefined,
    rightMin: Number.isFinite(rightMin) ? rightMin : undefined,
    rightMax: Number.isFinite(rightMax) ? rightMax : undefined
  };
  chart = sliceChartRange(chart, fromLabel, toLabel);
  return { chart, title: finalTitle };
}

export interface QuantileForecastOptions {
  names?: string[];
  column?: string;
  predictions?: string[];
  xAxisLabel?: string;
  xUnit?: string;
  yAxisLabel?: string;
  title?: string;
  // Report list grouping label (publish-time).
  category?: string;
}

/**
 * Builds a scatter chart: per file, Y = the percentile rank of the column's
 * LATEST value within that file's own history (0–100%), X = the user's manual
 * prediction for that file. One labelled point per file.
 */
export async function buildQuantileForecastChart(
  files: File[],
  options: QuantileForecastOptions = {}
): Promise<{ chart: ChartSpec; title: string }> {
  const { names, column, predictions, xAxisLabel, xUnit, yAxisLabel, title } = options;
  if (!column) {
    throw new Error("분석할 열(지표)을 선택하세요.");
  }

  const tables = await Promise.all(files.map(async (f) => await readWorkbookTable(await f.arrayBuffer())));
  const colIndex = (tables[0]?.columns ?? []).indexOf(column);
  const points: ScatterPoint[] = [];
  for (let i = 0; i < files.length; i++) {
    const name = names?.[i]?.trim() || files[i].name.replace(/\.[^.]+$/, "");
    const values = columnValues(tables[i], column, colIndex);
    if (!values || !values.length) {
      throw new Error(`"${name}"에서 '${column}' 데이터를 찾지 못했습니다.`);
    }
    const percentile = latestPercentile(values);
    const rawX = predictions?.[i];
    const x = rawX !== undefined && rawX.trim() !== "" ? Number(rawX) : NaN;
    if (!Number.isFinite(x)) {
      throw new Error(`"${name}"의 예측값(X)을 숫자로 입력하세요.`);
    }
    points.push({
      x,
      y: Math.round(percentile * 10) / 10,
      label: name,
      color: DUAL_AXIS_PALETTE[i % DUAL_AXIS_PALETTE.length]
    });
  }
  if (!points.length) {
    throw new Error("파일을 1개 이상 올리세요.");
  }

  const finalTitle = title?.trim() || `${column} 분위 vs 예측`;
  const chart: ChartSpec = {
    id: `quantile_${Date.now()}`,
    title: finalTitle,
    labels: [],
    series: [],
    kind: "scatter",
    points,
    xAxisLabel: xAxisLabel?.trim() || "예측값 (X)",
    xUnit: xUnit?.trim() || undefined,
    yAxisLabel: yAxisLabel?.trim() || `${column} 분위(%)`,
    yMin: 0,
    yMax: 100
  };
  return { chart, title: finalTitle };
}

export interface ScatterOptions {
  xColumn?: string;
  yColumn?: string;
  title?: string;
  xAxisLabel?: string;
  yAxisLabel?: string;
  // Report list grouping label (publish-time).
  category?: string;
}

// Report source tag for industry scatters. Each distinct chart is a separate
// slot keyed by title (so re-uploading overwrites just that one).
export const SCATTER_SOURCE = "산업 산점도";

// Category colour by keyword, so different files' category names map to the
// same colours: TMT navy, 制造 slate, 消费/医药 red, 周期 brown, 金融/地产 gold.
const SCATTER_FALLBACK = ["#0f766e", "#7c3aed", "#be123c", "#0891b2", "#64748b"];
export function scatterCategoryColor(category: string, fallbackIndex = 0): string {
  const c = category.trim();
  if (/tmt/i.test(c) || c.includes("科技")) return "#1e3a8a";
  if (c.includes("消费") || c.includes("医药")) return "#dc2626";
  if (c.includes("制造")) return "#93a8c4";
  if (c.includes("周期")) return "#92400e";
  if (c.includes("金融") || c.includes("地产")) return "#eab308";
  return SCATTER_FALLBACK[fallbackIndex % SCATTER_FALLBACK.length];
}

/**
 * Builds a single category-coloured scatter from one Excel. The admin picks the
 * X and Y numeric columns; one labelled point per 行业, coloured by 类别. The
 * category list rides in `series` (name + colour) as the legend.
 */
export async function buildScatterChart(
  file: File,
  options: ScatterOptions = {}
): Promise<{ chart: ChartSpec; title: string }> {
  const table = await parseScatterRows(await file.arrayBuffer());
  if (!table.names.length || !table.numericColumns.length) {
    throw new Error("산업 데이터를 찾지 못했습니다. (열: 类别 · 行业 · 그리고 숫자 지표 2개)");
  }
  const xCol = options.xColumn && table.valuesByColumn[options.xColumn] ? options.xColumn : table.numericColumns[0];
  const yCol =
    options.yColumn && table.valuesByColumn[options.yColumn]
      ? options.yColumn
      : table.numericColumns[1] ?? table.numericColumns[0];
  const xs = table.valuesByColumn[xCol] ?? [];
  const ys = table.valuesByColumn[yCol] ?? [];

  const cats = [...new Set(table.categories)];
  const catColor = new Map(cats.map((c, i) => [c, scatterCategoryColor(c, i)]));
  const points: ScatterPoint[] = [];
  table.names.forEach((name, i) => {
    const x = xs[i];
    const y = ys[i];
    if (!Number.isFinite(x) || !Number.isFinite(y)) return;
    points.push({ x, y, label: name, color: catColor.get(table.categories[i]) });
  });
  if (!points.length) throw new Error("선택한 X·Y 열에서 숫자 데이터를 찾지 못했습니다.");
  const legend: ChartSeries[] = cats.map((c) => ({ name: c, values: [], color: catColor.get(c) }));

  const title = options.title?.trim() || "산업 산점도";
  const chart: ChartSpec = {
    id: `scatter_${Date.now()}`,
    title,
    labels: [],
    series: legend,
    kind: "scatter",
    points,
    xAxisLabel: options.xAxisLabel?.trim() || `${xCol} (%)`,
    xUnit: "%",
    yAxisLabel: options.yAxisLabel?.trim() || `${yCol} (%)`
  };
  return { chart, title };
}

// Restricts a chart to the inclusive [fromLabel..toLabel] window on its label
// (time) axis. Order follows the data's own label order; reversed picks are
// normalised. Out-of-range labels are ignored.
function sliceChartRange(chart: ChartSpec, fromLabel?: string, toLabel?: string): ChartSpec {
  if (!fromLabel && !toLabel) return chart;
  const { labels } = chart;
  const fromIdx = fromLabel ? labels.indexOf(fromLabel) : 0;
  const toIdx = toLabel ? labels.indexOf(toLabel) : labels.length - 1;
  const a = fromIdx < 0 ? 0 : fromIdx;
  const b = toIdx < 0 ? labels.length - 1 : toIdx;
  const start = Math.min(a, b);
  const end = Math.max(a, b);
  if (start === 0 && end === labels.length - 1) return chart;
  return {
    ...chart,
    labels: labels.slice(start, end + 1),
    series: chart.series.map((series) => ({ ...series, values: series.values.slice(start, end + 1) }))
  };
}

function toSummary(report: ReportDetail): ReportSummary {
  return {
    report_id: report.report_id,
    title: report.title,
    status: report.status,
    created_at: report.created_at,
    published_at: report.published_at,
    source: report.source,
    category: report.category
  };
}

function countWeeks(points: PointMap): number {
  const dates = new Set<string>();
  for (const byDate of Object.values(points)) for (const date of Object.keys(byDate)) dates.add(date);
  return dates.size;
}

function lastWeekOf(points: PointMap): string | undefined {
  const dates = new Set<string>();
  for (const byDate of Object.values(points)) for (const date of Object.keys(byDate)) dates.add(date);
  return dates.size ? [...dates].sort().at(-1) : undefined;
}

function dedupeItems(items: ClusterItem[]): ClusterItem[] {
  const seen = new Set<string>();
  const out: ClusterItem[] = [];
  for (const it of items) {
    const key = `${it.industry_id}::${it.chart_id}`;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push({ industry_id: it.industry_id, chart_id: it.chart_id });
  }
  return out;
}
